/**
 * src/components/documento/HelpModal.tsx
 * =============================================================================
 * Modal "?" sencillo con instrucciones de uso del visor. Filosofía
 * [[feedback_simple_outside_complex_inside]]: la app es tan simple que con un
 * único modal de tips basta — no hace falta onboarding tour, settings ni docs.
 *
 * El padre controla open/close. Cerrar:
 *   - Click en X
 *   - Click en backdrop
 *   - Tecla Esc
 * =============================================================================
 */

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useAuth } from '@/components/providers/AuthProvider'
import { ContactoForm } from '@/components/documento/ContactoForm'
import { useFocusTrap } from '@/hooks/useFocusTrap'

interface Props {
  open: boolean
  onClose: () => void
}

export function HelpModal({ open, onClose }: Props) {
  // i18n: la ayuda la abre sobre todo quien recibe un enlace y no sabe qué es
  // esto. Es justo el momento en que menos se puede permitir estar en un
  // idioma que no entiende.
  const { t } = useTranslation()
  // El formulario de contacto solo existe para quien tiene cuenta. A quien no
  // la tiene no se le enseña: un formulario visible que al enviarlo dice
  // "regístrate" es peor que no ofrecerlo.
  const { user } = useAuth()
  /** Trampa de foco: Tab cicla dentro y al cerrar vuelve al disparador. */
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, open)
  // Esc cierra. Listener solo cuando está abierto para no contaminar la app.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-title"
    >
      <div
        // stopPropagation: click DENTRO del modal no debe cerrarlo.
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-zinc-200 p-6 space-y-4"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id="help-title" className="text-base font-semibold text-zinc-900">
            {/* Antes era "Cómo funciona" a pelo — el único texto del modal
                que no pasaba por i18n. Clave nueva en los seis common.json. */}
            {t('visor.ayuda.titulo')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('visor.herramientas.cerrar')}
            className="h-7 w-7 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-600"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <ul className="space-y-2.5 text-sm text-zinc-700 leading-relaxed">
          <Tip kbd={t('visor.teclas.rueda')}>{t('visor.ayuda.zoom')}</Tip>
          <Tip kbd={t('visor.teclas.espacio')}>{t('visor.ayuda.desplazar')}</Tip>
          <Tip kbd="↑ / ↓">{t('visor.ayuda.paginas')}</Tip>
          <Tip kbd="← / →">{t('visor.ayuda.versiones')}</Tip>
          <Tip kbd="M">{t('visor.ayuda.comentar')}</Tip>
          <Tip kbd="T">{t('visor.ayuda.texto')}</Tip>
          <Tip kbd="D / L / R / C">{t('visor.ayuda.formas')}</Tip>
          <Tip kbd="I">{t('visor.ayuda.imagen')}</Tip>
          <Tip kbd="V">{t('visor.ayuda.seleccionar')}</Tip>
          <Tip kbd="P">{t('visor.ayuda.laser')}</Tip>
          <Tip kbd={t('visor.teclas.supr')}>{t('visor.ayuda.borrar')}</Tip>
          <Tip kbd="Ctrl + Z">{t('visor.ayuda.deshacer')}</Tip>
          <Tip kbd={t('visor.teclas.arrastrar')}>{t('visor.ayuda.subir')}</Tip>
        </ul>

        {user && (
          <div className="mt-5 pt-4 border-t border-zinc-100">
            <ContactoForm />
          </div>
        )}
      </div>
    </div>
  )
}

interface TipProps {
  kbd: string
  children: React.ReactNode
}

function Tip({ kbd, children }: TipProps) {
  return (
    <li className="flex items-start gap-3">
      <kbd className="shrink-0 inline-flex items-center h-5 px-1.5 rounded bg-zinc-100 border border-zinc-200 text-[10px] font-medium text-zinc-700 tabular-nums">
        {kbd}
      </kbd>
      <span>{children}</span>
    </li>
  )
}
